import React from 'react';
import * as ReactDOM from 'react-dom';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridFilterCellProps,
} from '@progress/kendo-react-grid';
import { DataResult, State, toODataString } from '@progress/kendo-data-query';
import EmailDialog from '../../components/dialogs/EmailDialog/EmailDialog';
import { getEmails } from '../../store/email/sagas';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DateFormatEnum } from '../../store/user/types.d';
import DateFilterCell from '../DateFilterCell/DateFilterCell';
import { Role } from '../../store/conference/types';
import BodyCell from '../BodyCell/BodyCell';

interface EmailsLoaderProps {
  dataState: State;
  onDataReceived: (emails: DataResult) => void;
}

const EmailsLoader = (props: EmailsLoaderProps) => {
  const lastSuccess = React.useRef<string>('');
  const pending = React.useRef<string>('');

  const requestDataIfNeeded = () => {
    if (pending.current || toODataString(props.dataState) === lastSuccess.current) {
      return;
    }
    pending.current = toODataString(props.dataState);
    getEmails(pending).then((r) => {
      if (r.type === 'ok') {
        const json = r.value;
        lastSuccess.current = pending.current;
        pending.current = '';
        if (toODataString(props.dataState) === lastSuccess.current) {
          props.onDataReceived.call(undefined, {
            data: json.value,
            total: json['@count'],
          });
        } else {
          requestDataIfNeeded();
        }
      }
    });
  };

  requestDataIfNeeded();
  return pending.current ? <LoadingPanel /> : null;
};

const LoadingPanel = () => {
  const { t, i18n } = useTranslation();
  const loadingPanel = (
    <div className="k-loading-mask">
      <span className="k-loading-text">{t('Loading')}</span>
      <div className="k-loading-image" />
      <div className="k-loading-color" />
    </div>
  );
  const gridContent = document && document.querySelector('.k-grid-content');
  return gridContent ? ReactDOM.createPortal(loadingPanel, gridContent) : loadingPanel;
};

interface Props {
  dateFormat: string | null;
  role: Role;
}

const EmailHistory: React.FC<Props> = ({ dateFormat, role }) => {
  const { t, i18n } = useTranslation();
  const [emails, setEmails] = React.useState<DataResult>({
    data: [],
    total: 0,
  });
  const initialDataState = {
    take: 20,
    skip: 0,
  };
  const [dataState, setDataState] = React.useState<State>(initialDataState);
  const [dialog, setDialog] = React.useState<JSX.Element | undefined>(undefined);

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const dataReceived = (emails: DataResult) => {
    // Convert dates
    const data = emails.data.map((value) => ({
      ...value,
      created_at: new Date(value.created_at),
    }));
    setEmails({ ...emails, data });
  };

  // @ts-ignore
  return (
    <StyledWrapper>
      <Grid
        filterable={role.type == 'chair'}
        sortable={true}
        resizable={true}
        pageable={{
          buttonCount: 8,
          pageSizes: [10, 20, 50, 100],
        }}
        {...dataState}
        data={emails}
        onDataStateChange={dataStateChange}
      >
        {/* Check date format here: https://github.com/telerik/kendo-intl/blob/master/docs/date-formatting/index.md */}
        <Column
          field="created_at"
          title={t('Date')}
          filter="date"
          format={`{0:${dateFormat ?? DateFormatEnum.DMY24}}`}
          filterCell={(props: GridFilterCellProps) => <DateFilterCell {...props} />}
        />
        {/*<Column field="delivered" title={t('Delivered')} filter="boolean" />*/}
        <Column field="from_name" title={t('Triggered by')} />
        <Column field="recipient_name" title={t('Recipient name')} />
        <Column field="recipient_address" title={t('Recipient address')} />
        <Column field="subject" title={t('Subject')} />
        <Column
          field="body"
          title={t('Body')}
          cell={(props: GridCellProps) => (
            <BodyCell
              {...props}
              handleViewClick={(id) => setDialog(<EmailDialog id={id} onClose={() => setDialog(undefined)} />)}
            />
          )}
          filterable={false}
        />
      </Grid>
      <EmailsLoader dataState={dataState} onDataReceived={dataReceived} />
      {dialog}
    </StyledWrapper>
  );
};

export default EmailHistory;

const StyledWrapper = styled.div`
  height: 100%;

  .k-grid .k-grid-norecords td,
  .k-grid tr.k-alt td {
    background-color: #fafafa !important;
  }
  .k-grid tr.k-alt:hover td {
    background-color: #e4ebfd !important;
  }
  .k-grid td {
    border-color: #f4f5f9 !important;
    background-color: #fff;
  }
`;
